import { useState } from 'react';
import { useModalScrollLock } from '../../hooks/useModalScrollLock';
import styles from './CompleteProfileModal.module.css';

/**
 * CompleteProfileModal Component
 *
 * Modal shown to new users who signed up with email/password
 * to collect their full name since Auth0 doesn't ask for it during signup.
 *
 * Props:
 * - onComplete (function): Called when user successfully submits their name
 * - getAccessTokenSilently (function): Auth0 function to get access token
 */
function CompleteProfileModal({ onComplete, getAccessTokenSilently }) {
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Lock body scroll when modal is open (prevents background scrolling on mobile)
    // This modal is always "open" when rendered
    useModalScrollLock(true);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!name.trim()) {
            setError('Please enter your name');
            return;
        }

        if (name.includes('@')) {
            setError('Please enter your full name, not an email address');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const token = await getAccessTokenSilently();
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_BASE_URL}/auth/update-user-name`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ name: name.trim() })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to update name');
            }

            // Success! Call the completion callback
            onComplete();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalHeading}>
                    Welcome to Budget Tracker!
                </h2>
                <p className={styles.modalDescription}>
                    Please enter your full name to complete your profile.
                </p>

                <form onSubmit={handleSubmit}>
                    {error && (
                        <div className={styles.errorAlert}>
                            {error}
                        </div>
                    )}

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                            Full Name *
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Enter your full name"
                            disabled={loading}
                            autoFocus
                            className={styles.formInput}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={styles.submitButton}
                    >
                        {loading ? 'Saving...' : 'Continue'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CompleteProfileModal;
